import { FC, useState } from 'react';
import { useTranslation } from '@packages/utils';
import {
  useEditHostMutation,
  useGetEventHostOptionsQuery,
} from 'redux/apiSlice/events.slice';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { TRtkErrorType } from 'data/types/generic.types';
import EditableSelect from 'components/Base/EditableFields/EditableSelect';
import CreateOrganizationModal from 'components/Shared/CreateOrganizationModal';
import { TCreateOrganizationModalOpen } from 'components/Shared/CreateOrganizationModal/CreateOrganizationModal.types';
import { StarOutline } from 'assets/icon/icon';
import { Flex } from 'antd';
import { Text } from 'components/Base/Typography';
import { THostProps } from './Host.types';
import ConfirmModalEditHost from './ConfirmModalEditHost';

const Host: FC<THostProps> = ({ host, id }) => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.events.details.basic-info.host',
  });
  const { showToastNotification } = useShowToastNotification();
  const [openConfirmModal, setOpenConfirmModal] = useState({
    visible: false,
    newOrgId: '',
  });
  const [open, setOpen] = useState<TCreateOrganizationModalOpen>({
    visible: false,
    initialOrgName: '',
  });

  const [editHost, { isLoading: editHostLoading, isError: editHostError }] =
    useEditHostMutation();
  const { data: organizations } = useGetEventHostOptionsQuery();

  const onHostEdit = async (value: string) => {
    try {
      await editHost({
        id,
        value,
      }).unwrap();
      showToastNotification({
        message: t('SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
    } catch (err) {
      showToastNotification({
        message: (err as TRtkErrorType)?.data?.message,
        type: ENotificationType.ERROR,
      });
    }
  };

  const getNewOrganizationId = async (orgId: string) => {
    setOpenConfirmModal({
      visible: true,
      newOrgId: orgId,
    });
  };

  return (
    <>
      <Flex align="center" data-cy="event-host" gap={8}>
        <Flex align="center" gap={8} style={{ width: 160 }}>
          <StarOutline size={24} />
          <Text color="#808080">Host</Text>
        </Flex>
        <EditableSelect
          onEdit={onHostEdit}
          title={{
            label: host?.name || '',
            value: host?.id || '',
          }}
          isLoading={editHostLoading}
          options={organizations}
          addNewOption={{
            onClick: currentTypedValue => {
              setOpen({
                visible: true,
                initialOrgName: currentTypedValue || '',
              });
            },
            addButtonLabel: t('ADD'),
          }}
          emptyText={t('EMPTY_TEXT')}
          readonly={host?.allowedOperations.readonly}
          canRemove={host?.allowedOperations.canRemove}
          isError={editHostError}
          selectWidth={500}
        />
      </Flex>
      <CreateOrganizationModal
        open={open}
        setOpen={setOpen}
        attachOrganization={getNewOrganizationId}
      />
      <ConfirmModalEditHost
        open={openConfirmModal}
        setOpen={setOpenConfirmModal}
        onHostEdit={onHostEdit}
        editHostLoading={editHostLoading}
      />
    </>
  );
};

export default Host;
